import { auth } from "../firebaseConfig";
import { API_BASE_URL } from "./apiBase";

export class StudentAssessmentApiError extends Error {
  constructor(message, status = 0, code = "student-assessment-unavailable") {
    super(message);
    this.name = "StudentAssessmentApiError";
    this.status = status;
    this.code = code;
  }
}

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const exactFields = (value, fields) =>
  isPlainObject(value) &&
  Object.keys(value).length === fields.length &&
  fields.every((field) => Object.prototype.hasOwnProperty.call(value, field));

const validId = (value, maximumLength = 1500) =>
  typeof value === "string" &&
  value.length > 0 &&
  value === value.trim() &&
  value.length <= maximumLength &&
  !value.includes("/");

export const normalizeStudentQuizAccess = (access) => {
  if (
    exactFields(access, ["type", "assignmentId"]) &&
    access.type === "assignment" &&
    validId(access.assignmentId)
  ) {
    return { type: "assignment", assignmentId: access.assignmentId };
  }
  if (
    exactFields(access, ["type", "teacherUid", "quizCode"]) &&
    access.type === "quiz-code" &&
    validId(access.teacherUid, 128) &&
    typeof access.quizCode === "string" &&
    access.quizCode.length > 0 &&
    access.quizCode === access.quizCode.trim() &&
    access.quizCode.length <= 256
  ) {
    return { type: "quiz-code", teacherUid: access.teacherUid, quizCode: access.quizCode };
  }
  throw new StudentAssessmentApiError("The assessment access context is invalid.", 400, "invalid-access");
};

export const authenticatedRequest = async (path, options = {}) => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new StudentAssessmentApiError("Please sign in again to load assessment data.", 401, "authentication-required");
  }
  const token = await currentUser.getIdToken();
  return fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
};

const responseError = async (response, fallback) => {
  const data = await response.json().catch(() => ({}));
  const error = new StudentAssessmentApiError(
    data.error || fallback,
    response.status,
    data.code || "student-assessment-unavailable"
  );
  // A versioned submit refused because answers changed elsewhere carries the
  // server's current answers, so the page can show them for review.
  if (data.code === "answers-changed") {
    try {
      error.details = {
        answers: normalizeAnswerMap(data.answers),
        answerRevisions: normalizeRevisionMap(data.answerRevisions),
      };
    } catch (_invalidDetails) {
      error.details = null;
    }
  }
  // Drawing refusals carry the questions to review; the drawing hook reloads
  // authoritative drawing state itself, so only the IDs are kept here.
  if (data.code === "drawing-drafts-pending") {
    error.details = {
      drawingQuestionIds: Array.isArray(data.drawingQuestionIds)
        ? data.drawingQuestionIds.filter((questionId) => validId(questionId))
        : [],
    };
  }
  return error;
};

const validRevision = (value) => Number.isSafeInteger(value) && value >= 0;

function normalizeAnswerMap(value) {
  if (!isPlainObject(value) || Object.entries(value).some(([questionId, text]) =>
    !validId(questionId) || typeof text !== "string")) {
    throw new StudentAssessmentApiError("The server returned invalid answers.", 502, "invalid-response");
  }
  return { ...value };
}

function normalizeRevisionMap(value) {
  if (!isPlainObject(value) || Object.entries(value).some(([questionId, revision]) =>
    !validId(questionId) || !validRevision(revision))) {
    throw new StudentAssessmentApiError("The server returned invalid answer revisions.", 502, "invalid-response");
  }
  return { ...value };
}

const normalizeMcqOptions = (options) => {
  if (!Array.isArray(options) || options.length < 2 || options.length > 6) {
    throw new StudentAssessmentApiError("The server returned invalid multiple-choice options.", 502, "invalid-response");
  }
  const normalized = options.map((option) => {
    if (
      !exactFields(option, ["id", "text"]) ||
      !validId(option.id) ||
      typeof option.text !== "string" ||
      !option.text.trim()
    ) {
      throw new StudentAssessmentApiError("The server returned an invalid multiple-choice option.", 502, "invalid-response");
    }
    return { id: option.id, text: option.text };
  });
  if (new Set(normalized.map((option) => option.id)).size !== normalized.length) {
    throw new StudentAssessmentApiError("The server returned duplicate multiple-choice option IDs.", 502, "invalid-response");
  }
  return normalized;
};

export const normalizeStudentQuestion = (question) => {
  const isMultipleChoice = question?.questionType === "multipleChoice";
  const fields = [
    "id", "questionType", "questionText", "questionImageURL", "maxMark",
    ...(question?.questionMedia ? ["questionMedia"] : []),
    ...(isMultipleChoice ? ["options"] : []),
  ];
  if (
    !exactFields(question, fields) ||
    !validId(question.id) ||
    typeof question.questionType !== "string" ||
    !question.questionType.trim() ||
    typeof question.questionText !== "string" ||
    typeof question.questionImageURL !== "string" ||
    (question.questionMedia && (!exactFields(question.questionMedia, ["mediaId"]) ||
      !validId(question.questionMedia.mediaId))) ||
    !Number.isFinite(question.maxMark) ||
    question.maxMark < 0
  ) {
    throw new StudentAssessmentApiError("The server returned an invalid assessment question.", 502, "invalid-response");
  }
  return isMultipleChoice
    ? { ...question, options: normalizeMcqOptions(question.options) }
    : { ...question };
};

export const fetchStudentQuizQuestions = async (access, options = {}) => {
  const normalizedAccess = normalizeStudentQuizAccess(access);
  const response = await authenticatedRequest("/api/student-quiz/questions", {
    method: "POST",
    body: JSON.stringify({ access: normalizedAccess }),
    signal: options.signal,
  });
  if (!response.ok) throw await responseError(response, "Assessment questions could not be loaded.");
  const data = await response.json().catch(() => null);
  if (
    !exactFields(data, ["ok", "quiz", "questions"]) ||
    data.ok !== true ||
    !exactFields(data.quiz, ["id"]) ||
    !validId(data.quiz.id) ||
    !Array.isArray(data.questions)
  ) {
    throw new StudentAssessmentApiError("The server returned an invalid assessment response.", 502, "invalid-response");
  }
  const questions = data.questions.map(normalizeStudentQuestion);
  if (new Set(questions.map((question) => question.id)).size !== questions.length) {
    throw new StudentAssessmentApiError("The server returned duplicate assessment questions.", 502, "invalid-response");
  }
  return { quiz: { id: data.quiz.id }, questions };
};

const nullableIsoDate = (value) => {
  if (value === null) return null;
  if (typeof value !== "string") return undefined;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : undefined;
};

const normalizeReattempt = (value) => {
  if (
    !exactFields(value, ["available", "remaining", "reason", "action"]) ||
    typeof value.available !== "boolean" ||
    !Number.isSafeInteger(value.remaining) ||
    value.remaining < 0 ||
    (value.reason !== null && typeof value.reason !== "string") ||
    (value.action !== null && !["required", "available"].includes(value.action)) ||
    (value.action !== null && !value.available)
  ) {
    throw new StudentAssessmentApiError("The server returned invalid reattempt state.", 502, "invalid-response");
  }
  return value;
};

const normalizeAttemptStateResponse = (data, modes, versioned = false) => {
  if (
    !exactFields(data, ["ok", "mode", "quizId", "session", "reattempt"]) ||
    data.ok !== true ||
    !modes.includes(data.mode) ||
    !validId(data.quizId) ||
    (data.session === null && !["terminal", "not-started"].includes(data.mode))
  ) {
    throw new StudentAssessmentApiError("The server returned an invalid attempt response.", 502, "invalid-response");
  }
  const reattempt = normalizeReattempt(data.reattempt);
  if (data.session === null) return { ...data, reattempt };
  const session = data.session;
  const startedAt = nullableIsoDate(session?.startedAt);
  const expiresAt = nullableIsoDate(session?.expiresAt);
  if (
    !exactFields(session, [
      "id", "quizId", "status", "attemptNumber", "startedAt", "expiresAt",
      "timeLimitMinutes", "answers", ...(versioned ? ["answerRevisions"] : []),
    ]) ||
    !validId(session.id) ||
    session.quizId !== data.quizId ||
    typeof session.status !== "string" ||
    !Number.isSafeInteger(session.attemptNumber) ||
    session.attemptNumber < 1 ||
    startedAt === undefined ||
    expiresAt === undefined ||
    !Number.isFinite(session.timeLimitMinutes) ||
    session.timeLimitMinutes < 0 ||
    !isPlainObject(session.answers)
  ) {
    throw new StudentAssessmentApiError("The server returned an invalid attempt session.", 502, "invalid-response");
  }
  return {
    ...data,
    reattempt,
    session: {
      ...session,
      startedAt,
      expiresAt,
      ...(versioned ? { answerRevisions: normalizeRevisionMap(session.answerRevisions) } : {}),
    },
  };
};

// `options.versioned` asks for per-question answer revisions (the versioned
// save contract); without it the response keeps its original shape.
const attemptStateBody = (access, quizId, options) => JSON.stringify({
  access: normalizeStudentQuizAccess(access),
  quizId,
  ...(options.versioned ? { answerContract: 2 } : {}),
});

export const startOrResumeStudentQuizAttempt = async (access, quizId, options = {}) => {
  normalizeStudentQuizAccess(access);
  if (!validId(quizId)) {
    throw new StudentAssessmentApiError("The assessment ID is invalid.", 400, "invalid-quiz-id");
  }
  const response = await authenticatedRequest("/api/quiz-attempts/start-or-resume", {
    method: "POST",
    body: attemptStateBody(access, quizId, options),
    signal: options.signal,
  });
  if (!response.ok) throw await responseError(response, "The assessment attempt could not be started.");
  const data = await response.json().catch(() => null);
  return normalizeAttemptStateResponse(data, ["created", "resumed", "terminal", "awaiting-result"], options.versioned === true);
};

export const fetchStudentQuizAttemptStatus = async (access, quizId, options = {}) => {
  normalizeStudentQuizAccess(access);
  if (!validId(quizId)) {
    throw new StudentAssessmentApiError("The assessment ID is invalid.", 400, "invalid-quiz-id");
  }
  const response = await authenticatedRequest("/api/quiz-attempts/status", {
    method: "POST",
    body: attemptStateBody(access, quizId, options),
    signal: options.signal,
  });
  if (!response.ok) throw await responseError(response, "The assessment attempt status could not be loaded.");
  const data = await response.json().catch(() => null);
  return normalizeAttemptStateResponse(data, ["not-started", "resumed", "terminal", "awaiting-result"], options.versioned === true);
};

export const submitStudentQuizAttempt = async (attemptId, answers, finalStatus, options = {}) => {
  if (!validId(attemptId) || !isPlainObject(answers)) {
    throw new StudentAssessmentApiError("The assessment submission is invalid.", 400, "invalid-submission");
  }
  for (const [questionId, answer] of Object.entries(answers)) {
    if (!validId(questionId) || typeof answer !== "string") {
      throw new StudentAssessmentApiError("The assessment answers are invalid.", 400, "invalid-answers");
    }
  }
  if (finalStatus !== "submitted" && finalStatus !== "expired") {
    throw new StudentAssessmentApiError("The assessment final status is invalid.", 400, "invalid-final-status");
  }
  const response = await authenticatedRequest("/api/quiz-attempts/submit", {
    method: "POST",
    body: JSON.stringify({ attemptId, answers, finalStatus }),
    signal: options.signal,
  });
  if (!response.ok) throw await responseError(response, "The assessment could not be submitted.");
  const data = await response.json().catch(() => null);
  if (
    !exactFields(data, ["ok", "mode", "attemptId", "attemptNumber", "status", "submissionIds"]) ||
    data.ok !== true ||
    !["finalized", "idempotent"].includes(data.mode) ||
    data.attemptId !== attemptId ||
    !Number.isSafeInteger(data.attemptNumber) ||
    data.attemptNumber < 1 ||
    !["submitted", "expired"].includes(data.status) ||
    !Array.isArray(data.submissionIds) ||
    data.submissionIds.some((id) => !validId(id))
  ) {
    throw new StudentAssessmentApiError("The server returned an invalid submission response.", 502, "invalid-response");
  }
  return data;
};

export const saveStudentQuizAttempt = async (attemptId, answers, options = {}) => {
  if (!validId(attemptId) || !isPlainObject(answers) ||
      Object.entries(answers).some(([questionId, answer]) => !validId(questionId) || typeof answer !== "string")) {
    throw new StudentAssessmentApiError("The assessment answers are invalid.", 400, "invalid-answers");
  }
  const response = await authenticatedRequest("/api/quiz-attempts/save", {
    method: "POST",
    body: JSON.stringify({ attemptId, answers }),
    signal: options.signal,
  });
  if (!response.ok) throw await responseError(response, "The assessment answers could not be saved.");
  const data = await response.json().catch(() => null);
  if (!exactFields(data, ["ok"]) || data.ok !== true) {
    throw new StudentAssessmentApiError("The server returned an invalid save response.", 502, "invalid-response");
  }
  return data;
};

const SAVE_RESULT_STATUSES = ["saved", "conflict"];

// Versioned per-question save: each change carries the revision the page last
// saw. Returns { results: { questionId: { status, revision, text? } } }.
export const saveStudentQuizAnswerChanges = async (attemptId, changes, options = {}) => {
  const entries = isPlainObject(changes) ? Object.entries(changes) : [];
  if (!validId(attemptId) || entries.length === 0 || entries.some(([questionId, change]) =>
    !validId(questionId) || !exactFields(change, ["text", "baseRevision"]) ||
    typeof change.text !== "string" || !validRevision(change.baseRevision))) {
    throw new StudentAssessmentApiError("The assessment answers are invalid.", 400, "invalid-answers");
  }
  const response = await authenticatedRequest("/api/quiz-attempts/save", {
    method: "POST",
    body: JSON.stringify({ attemptId, changes }),
    signal: options.signal,
  });
  if (!response.ok) throw await responseError(response, "The assessment answers could not be saved.");
  const data = await response.json().catch(() => null);
  const results = data?.results;
  if (
    !exactFields(data, ["ok", "results"]) || data.ok !== true || !isPlainObject(results) ||
    entries.some(([questionId]) => !isPlainObject(results[questionId])) ||
    Object.entries(results).some(([questionId, result]) =>
      !Object.prototype.hasOwnProperty.call(changes, questionId) ||
      !SAVE_RESULT_STATUSES.includes(result.status) ||
      !validRevision(result.revision) ||
      (result.status === "conflict" ? typeof result.text !== "string" : "text" in result))
  ) {
    throw new StudentAssessmentApiError("The server returned an invalid save response.", 502, "invalid-response");
  }
  return { results };
};

// Versioned submit: the server finalizes its own saved answers after checking
// they are still the revisions this page last saw. `options.drawingRevisions`
// adds the same check for committed drawings.
export const submitStudentQuizAnswersByRevision = async (attemptId, answerRevisions, finalStatus, options = {}) => {
  const { drawingRevisions } = options;
  const invalidRevisions = (map) => !isPlainObject(map) ||
    Object.entries(map).some(([questionId, revision]) => !validId(questionId) || !validRevision(revision));
  if (!validId(attemptId) || invalidRevisions(answerRevisions) ||
      (drawingRevisions !== undefined && invalidRevisions(drawingRevisions)) ||
      (finalStatus !== "submitted" && finalStatus !== "expired")) {
    throw new StudentAssessmentApiError("The assessment submission is invalid.", 400, "invalid-submission");
  }
  const response = await authenticatedRequest("/api/quiz-attempts/submit", {
    method: "POST",
    body: JSON.stringify({
      attemptId, answerRevisions, finalStatus, ...(drawingRevisions !== undefined ? { drawingRevisions } : {}),
    }),
    signal: options.signal,
  });
  if (!response.ok) throw await responseError(response, "The assessment could not be submitted.");
  const data = await response.json().catch(() => null);
  if (
    !exactFields(data, ["ok", "mode", "attemptId", "attemptNumber", "status", "submissionIds"]) ||
    data.ok !== true ||
    !["finalized", "idempotent"].includes(data.mode) ||
    data.attemptId !== attemptId ||
    !Number.isSafeInteger(data.attemptNumber) ||
    data.attemptNumber < 1 ||
    !["submitted", "expired"].includes(data.status) ||
    !Array.isArray(data.submissionIds) ||
    data.submissionIds.some((id) => !validId(id))
  ) {
    throw new StudentAssessmentApiError("The server returned an invalid submission response.", 502, "invalid-response");
  }
  return data;
};

const normalizeUnavailableAttempt = (entry) => {
  if (!exactFields(entry, ["attemptId", "quizId", "code"]) || !validId(entry.attemptId) ||
      (entry.quizId !== null && !validId(entry.quizId)) || typeof entry.code !== "string") {
    throw new StudentAssessmentApiError("The server returned an invalid assessment status response.", 502, "invalid-response");
  }
  return { attemptId: entry.attemptId, quizId: entry.quizId, code: entry.code };
};

// Finalizes attempts past their cutoff. `unavailable` lists open attempts the
// server could not finalize safely (left unchanged and retried next time):
// their status is unknown and must be shown as such. A server without this
// contract fails the whole request instead, so its `unavailable` is empty.
export const reconcileExpiredQuizAttempts = async (options = {}) => {
  const response = await authenticatedRequest("/api/quiz-attempts/reconcile", {
    method: "POST",
    body: JSON.stringify({ reconcileContract: 2 }),
    signal: options.signal,
  });
  if (!response.ok) throw await responseError(response, "Overdue assessments could not be checked.");
  const data = await response.json().catch(() => null);
  const reportsUnavailable = exactFields(data, ["ok", "reconciled", "unavailable"]);
  if ((!reportsUnavailable && !exactFields(data, ["ok", "reconciled"])) || data.ok !== true ||
      !Number.isSafeInteger(data.reconciled) || data.reconciled < 0 ||
      (reportsUnavailable && !Array.isArray(data.unavailable))) {
    throw new StudentAssessmentApiError("The server returned an invalid assessment status response.", 502, "invalid-response");
  }
  return {
    ok: true,
    reconciled: data.reconciled,
    unavailable: reportsUnavailable ? data.unavailable.map(normalizeUnavailableAttempt) : [],
  };
};

// Optional on any result: the authoritative summary of a submitted drawing.
const normalizeDrawingAnswer = (value) => {
  if (!exactFields(value, ["source", "revision"]) ||
      !["committed", "automatic-checkpoint"].includes(value.source) || !validRevision(value.revision)) {
    throw new StudentAssessmentApiError("The server returned an invalid drawing answer.", 502, "invalid-response");
  }
  return { source: value.source, revision: value.revision };
};

// Shared by every validation, so they are frozen: each result builds its own
// field list from them and never changes these.
const RESULT_BASE_FIELDS = Object.freeze([
  "id", "attemptId", "attemptNumber", "quizId", "questionId", "quizCode", "userId", "questionType",
  "questionText", "questionImageURL", "userAnswer", "maxMark", "markAwarded", "submittedAt", "status",
]);
const PROTECTED_RESULT_FIELDS = Object.freeze(["feedback", "markSchemeAnswer", "studentModelAnswer", "modelAnswer"]);

const normalizeResultOption = (option) => {
  if (
    !exactFields(option, ["label", "text"]) ||
    typeof option.label !== "string" ||
    !/^[A-F]$/.test(option.label) ||
    typeof option.text !== "string" ||
    !option.text.trim()
  ) {
    throw new StudentAssessmentApiError("The server returned an invalid multiple-choice result option.", 502, "invalid-response");
  }
  return { label: option.label, text: option.text };
};

const normalizeMcqResult = (mcqResult, marked, feedbackReleased) => {
  const fields = feedbackReleased
    ? ["selectedOption", "correctOption", "selectedAnswerUnavailable", "reviewRequired"]
    : ["selectedOption", "selectedAnswerUnavailable", "reviewRequired"];
  if (
    !exactFields(mcqResult, fields) ||
    (mcqResult.selectedOption !== null && !isPlainObject(mcqResult.selectedOption)) ||
    (feedbackReleased && mcqResult.correctOption !== null && !isPlainObject(mcqResult.correctOption)) ||
    typeof mcqResult.selectedAnswerUnavailable !== "boolean" ||
    typeof mcqResult.reviewRequired !== "boolean" ||
    (feedbackReleased && !marked && mcqResult.correctOption !== null)
  ) {
    throw new StudentAssessmentApiError("The server returned an invalid multiple-choice result.", 502, "invalid-response");
  }
  return {
    selectedOption: mcqResult.selectedOption === null
      ? null
      : normalizeResultOption(mcqResult.selectedOption),
    ...(feedbackReleased ? {
      correctOption: mcqResult.correctOption === null
        ? null
        : normalizeResultOption(mcqResult.correctOption),
    } : {}),
    selectedAnswerUnavailable: mcqResult.selectedAnswerUnavailable,
    reviewRequired: mcqResult.reviewRequired,
  };
};

const nullableString = (value) => value === null || typeof value === "string";

export const normalizeStudentResult = (result, feedbackReleased = true) => {
  const markIsValid = result?.markAwarded === "Pending" || Number.isFinite(result?.markAwarded);
  const isMultipleChoice = result?.questionType === "multipleChoice";
  const fields = [
    ...RESULT_BASE_FIELDS,
    ...(feedbackReleased ? PROTECTED_RESULT_FIELDS : []),
    ...(isMultipleChoice ? ["mcqResult"] : []),
    ...(result?.questionMedia ? ["questionMedia"] : []),
    ...(result?.drawingAnswer ? ["drawingAnswer"] : []),
  ];
  // Preserve the legacy results-page fallback for old submissions that predate
  // the submittedAt field while still validating all supplied timestamps.
  const submittedAt = result?.submittedAt === null ? new Date() : new Date(result?.submittedAt);
  if (
    !exactFields(result, fields) ||
    !validId(result.id) ||
    (result.attemptId !== null && !validId(result.attemptId)) ||
    !Number.isSafeInteger(result.attemptNumber) ||
    result.attemptNumber < 1 ||
    !validId(result.questionId) ||
    typeof result.quizId !== "string" ||
    typeof result.quizCode !== "string" ||
    typeof result.userId !== "string" ||
    typeof result.questionType !== "string" ||
    typeof result.questionText !== "string" ||
    typeof result.questionImageURL !== "string" ||
    (result.questionMedia && (!exactFields(result.questionMedia, ["mediaId"]) ||
      !validId(result.questionMedia.mediaId))) ||
    typeof result.userAnswer !== "string" ||
    (feedbackReleased && typeof result.feedback !== "string") ||
    !Number.isFinite(result.maxMark) ||
    !markIsValid ||
    typeof result.status !== "string" ||
    (feedbackReleased && !nullableString(result.markSchemeAnswer)) ||
    (feedbackReleased && !nullableString(result.studentModelAnswer)) ||
    (feedbackReleased && !nullableString(result.modelAnswer)) ||
    !Number.isFinite(submittedAt.getTime())
  ) {
    throw new StudentAssessmentApiError("The server returned an invalid student result.", 502, "invalid-response");
  }
  if (result.drawingAnswer) result = { ...result, drawingAnswer: normalizeDrawingAnswer(result.drawingAnswer) };
  return isMultipleChoice
    ? {
        ...result,
        mcqResult: normalizeMcqResult(
          result.mcqResult,
          result.markAwarded !== "Pending",
          feedbackReleased
        ),
        submittedAt,
      }
    : { ...result, submittedAt };
};

const normalizeStudentAttempt = (attempt) => {
  if (
    !exactFields(attempt, ["id", "quizId", "attemptNumber", "status", "startedAt", "submittedAt", "attemptOutcome"]) ||
    !validId(attempt.id) || !validId(attempt.quizId) ||
    !Number.isSafeInteger(attempt.attemptNumber) || attempt.attemptNumber < 1 ||
    !["in_progress", "submitted", "expired"].includes(attempt.status) ||
    (attempt.attemptOutcome !== null && !isPlainObject(attempt.attemptOutcome))
  ) throw new StudentAssessmentApiError("The server returned an invalid assessment attempt.", 502, "invalid-response");
  const startedAt = attempt.startedAt === null ? null : new Date(attempt.startedAt);
  const submittedAt = attempt.submittedAt === null ? null : new Date(attempt.submittedAt);
  if ((startedAt && !Number.isFinite(startedAt.getTime())) || (submittedAt && !Number.isFinite(submittedAt.getTime()))) {
    throw new StudentAssessmentApiError("The server returned an invalid attempt timestamp.", 502, "invalid-response");
  }
  return { ...attempt, startedAt, submittedAt };
};

const normalizeFeedbackAccess = (access) => {
  const baseFields = [
    "quizId", "quizCode", "status", "mode", "action", "available", "remaining", "reason",
  ];
  const hasLaunchAccess = Object.prototype.hasOwnProperty.call(access || {}, "launchAccess");
  if (
    !exactFields(access, hasLaunchAccess ? [...baseFields, "launchAccess"] : baseFields) ||
    typeof access.quizId !== "string" ||
    typeof access.quizCode !== "string" ||
    !["locked", "released"].includes(access.status) ||
    (access.mode !== null && !["disabled", "always", "belowPercentage", "belowGrade"].includes(access.mode)) ||
    (access.action !== null && !["required", "available"].includes(access.action)) ||
    typeof access.available !== "boolean" ||
    !Number.isSafeInteger(access.remaining) || access.remaining < 0 ||
    typeof access.reason !== "string" ||
    (access.action !== null && !access.available) ||
    (access.status === "released" && access.available)
  ) {
    throw new StudentAssessmentApiError("The server returned invalid feedback access state.", 502, "invalid-response");
  }
  const launchAccess = hasLaunchAccess && access.launchAccess !== null
    ? normalizeStudentQuizAccess(access.launchAccess)
    : null;
  return { ...access, launchAccess };
};

const feedbackAccessKey = ({ quizId, quizCode }) => {
  const id = String(quizId || "").trim();
  return id ? `quizId:${id}` : `quizCode:${String(quizCode || "").trim()}`;
};

export const fetchStudentAssessmentResults = async (options = {}) => {
  const response = await authenticatedRequest("/api/student-results", {
    method: "GET",
    signal: options.signal,
  });
  if (!response.ok) throw await responseError(response, "Student results could not be loaded.");
  const data = await response.json().catch(() => null);
  if (
    !exactFields(data, ["ok", "results", "finalResults", "attempts", "feedbackAccess"]) ||
    data.ok !== true ||
    !Array.isArray(data.results) ||
    !Array.isArray(data.finalResults) ||
    !Array.isArray(data.attempts) ||
    !Array.isArray(data.feedbackAccess)
  ) {
    throw new StudentAssessmentApiError("The server returned an invalid results response.", 502, "invalid-response");
  }
  const feedbackAccess = data.feedbackAccess.map(normalizeFeedbackAccess);
  const accessByKey = new Map(feedbackAccess.map((access) => [feedbackAccessKey(access), access]));
  const results = data.results.map((result) => {
    const access = accessByKey.get(feedbackAccessKey(result));
    if (!access) {
      throw new StudentAssessmentApiError("The server omitted feedback access state.", 502, "invalid-response");
    }
    return normalizeStudentResult(result, access.status === "released");
  });
  return {
    results,
    finalResults: data.finalResults,
    attempts: data.attempts.map(normalizeStudentAttempt),
    feedbackAccess,
  };
};

export const fetchStudentResults = async (options = {}) =>
  (await fetchStudentAssessmentResults(options)).results;
